import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, IconButton, Portal, TextField, Tooltip } from '@mui/material';
import {
    AutoAwesome as AutoAwesomeIcon,
    Cancel as CancelIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    DescriptionOutlined as DescriptionIcon,
    ErrorOutline as ErrorOutlineIcon,
    InfoOutlined as InfoIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

import DocumentService from '../../../../Services/documents.service.js';
import EnumsService from '../../../../Services/enums.service.js';
import { calculateVatAmountV2 } from '../../../../Utils/GetVatValue.js';
import DocumentDrop from '../../../InventoryManagement/DocumentDrop';
import AddAccountCardModal from '../../../Widgets/AddAccountCardModal/AddAccountCardModal';
import CustomDatePicker from '../../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker.js';
import CustomMonthDatePicker from '../../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import { StyledPopper } from './StyledComponents';
import styles from './ImportManifestEntryModal.module.scss';

const DOC_KINDS = ['manifest', 'invoice'];

const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/tiff'];
const ACCEPTED_EXT_RE = /\.(pdf|png|jpe?g|webp|tiff?)$/i;

const MANIFEST_COMPONENT_FIELDS = ['customsPurchaseTax', 'importManifestVat', 'computerFee', 'securityCheckFee', 'variousFees'];

const AMOUNT_FIELDS = [
    'commission', 'storageAtCustoms', 'seaAirFreight', 'variousVatLiable', 'variousVatExempt', 'roundingDifference',
    ...MANIFEST_COMPONENT_FIELDS, 'totalManifest',
];

const toNumber = (value) => parseFloat(value) || 0;

const formatAmount = (value) =>
    toNumber(value).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatFileSize = (bytes) => {
    if (!Number.isFinite(bytes)) return '';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** "dd/MM/yyyy" from the server -> UTC midnight Date (what CustomDatePicker emits). */
const parseServerDate = (value) => {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(value || '').trim());
    return match ? new Date(Date.UTC(Number(match[3]), Number(match[2]) - 1, Number(match[1]))) : null;
};

const isAcceptedFile = (file) => ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXT_RE.test(file.name || '');

const isImageFile = (file) => String(file?.type || '').startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(file?.name || '');

/** Day 10 of the month in UTC, matching the backend's reporting-month convention. */
const toMonthDate = (year, monthIndex) => new Date(Date.UTC(year, monthIndex, 10));

/** Totals derived from the entered amounts (the manifest total follows its components). */
const withTotals = (data, changedField) => {
    const next = { ...data };
    if (!changedField || MANIFEST_COMPONENT_FIELDS.includes(changedField)) {
        next.totalManifest = MANIFEST_COMPONENT_FIELDS.reduce((sum, f) => sum + toNumber(next[f]), 0).toFixed(2);
    }
    const totalVatLiable = toNumber(next.variousVatLiable) + toNumber(next.storageAtCustoms) + toNumber(next.commission);
    const totalIncludingVat = toNumber(next.seaAirFreight) + toNumber(next.variousVatExempt)
        + toNumber(next.totalManifest) + toNumber(next.roundingDifference);
    const totalVat = calculateVatAmountV2(totalVatLiable, 100, next.invoiceDate);
    next.totalVatLiable = totalVatLiable.toFixed(2);
    next.totalIncludingVat = totalIncludingVat.toFixed(2);
    next.totalVat = totalVat.toFixed(2);
    next.totalToPay = (totalIncludingVat + totalVatLiable + totalVat).toFixed(2);
    return next;
};

const buildInitialFormData = (year, month) => ({
    expense: '',
    inputVat: '3',
    customsAgent: '',
    customsAgentId: '',

    invoiceNumber: '',
    invoiceDate: new Date(),
    commission: '0.00',
    storageAtCustoms: '0.00',
    seaAirFreight: '0.00',
    variousVatLiable: '0.00',
    variousVatExempt: '0.00',
    roundingDifference: '0.00',

    manifest: '350',
    liableExpenses: '200',
    exemptExpenses: '211',

    manifestNumber: '',
    manifestDate: new Date(),
    customsPurchaseTax: '0.00',
    importManifestVat: '0.00',
    computerFee: '0.00',
    securityCheckFee: '0.00',
    variousFees: '0.00',
    totalManifest: '0.00',
    totalIncludingVat: '0.00',
    totalVatLiable: '0.00',
    totalVat: '0.00',
    totalToPay: '0.00',

    reportingMonthDate: toMonthDate(year, month - 1),
    registrationMonthDate: toMonthDate(year, month - 1),
});

const EMPTY_READ_STATE = { status: 'idle', error: '', warnings: [] };

const ImportManifestEntryModal = ({
    open,
    onClose,
    direction = 'rtl',
    t,
    client,
    token,
    accountCardOptions = [],
    sortCodeIdOptions = [],
    year,
    month,
    onDataRefresh,
    fetchEnums,
    setLoading,
    setLocalLoading,
    palCodes = [],
    onAccountCardAdded,
}) => {
    const [stepIndex, setStepIndex] = useState(0);
    const [formData, setFormData] = useState(() => buildInitialFormData(year, month));
    const [files, setFiles] = useState({ manifest: [], invoice: [] });
    const [readState, setReadState] = useState({ manifest: EMPTY_READ_STATE, invoice: EMPTY_READ_STATE });
    const [aiFields, setAiFields] = useState(() => new Set());
    const [isReading, setIsReading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [showPreview, setShowPreview] = useState(true);
    const [previewFile, setPreviewFile] = useState(null);
    const scrollRef = useRef(null);
    const fileUrlsRef = useRef(new Map());

    // AddAccountCardModal
    const [addAccountCardOpen, setAddAccountCardOpen] = useState(false);
    const [addAccountCardType, setAddAccountCardType] = useState('expense');
    const [cardIdOption, setCardIdOption] = useState(1);
    const [newAccountCard, setNewAccountCard] = useState({ accountCodeName: '', accountCodeId: '', accountCaseId: '' });
    const accountSearchRef = useRef({});

    const allFiles = useMemo(
        () => DOC_KINDS.flatMap((kind) => files[kind].map((file) => ({ kind, file }))),
        [files]
    );
    const hasFiles = allFiles.length > 0;
    const activePreview = allFiles.find((entry) => entry.file === previewFile) || allFiles[0] || null;
    const hasPreview = showPreview && !!activePreview;
    const isBusy = isReading || isSubmitting;

    const getFileUrl = useCallback((file) => {
        const cache = fileUrlsRef.current;
        if (!cache.has(file)) cache.set(file, URL.createObjectURL(file));
        return cache.get(file);
    }, []);

    // Revoke URLs of files that are no longer selected.
    useEffect(() => {
        const cache = fileUrlsRef.current;
        const current = new Set(allFiles.map((entry) => entry.file));
        cache.forEach((url, file) => {
            if (!current.has(file)) {
                URL.revokeObjectURL(url);
                cache.delete(file);
            }
        });
    }, [allFiles]);

    useEffect(() => () => {
        fileUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        fileUrlsRef.current.clear();
    }, []);

    useEffect(() => {
        if (!year || !month) return;
        setFormData((prev) => ({
            ...prev,
            reportingMonthDate: toMonthDate(year, month - 1),
            registrationMonthDate: toMonthDate(year, month - 1),
        }));
    }, [year, month]);

    useEffect(() => {
        if (!open || !client?.caseId || !token) {
            setCardIdOption(1);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            try {
                const nextId = await EnumsService.GetNextCardId(client.caseId, null, token);
                if (!cancelled) setCardIdOption(nextId);
            } catch {
                if (!cancelled) setCardIdOption(1);
            }
        })();
        return () => { cancelled = true; };
    }, [open, client?.caseId, token, accountCardOptions.length]);

    // ───────────── form state ─────────────

    const handleInputChange = useCallback((field, value) => {
        setFormData((prev) => withTotals({ ...prev, [field]: value }, field));
        setAiFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
        setValidationErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    const handleMonthChange = (field) => (date) => {
        if (!date) return;
        handleInputChange(field, toMonthDate(date.getUTCFullYear(), date.getUTCMonth()));
    };

    const resetAll = useCallback(() => {
        setFormData(buildInitialFormData(year, month));
        setFiles({ manifest: [], invoice: [] });
        setReadState({ manifest: EMPTY_READ_STATE, invoice: EMPTY_READ_STATE });
        setAiFields(new Set());
        setValidationErrors({});
        setPreviewFile(null);
        setStepIndex(0);
    }, [year, month]);

    // ───────────── documents ─────────────

    const handleFilesChange = (kind) => (event) => {
        const incoming = Array.from(event?.target?.files ?? []);
        const accepted = incoming.filter(isAcceptedFile);
        if (accepted.length < incoming.length) {
            toast.error(t('importManifestModal.unsupportedType'));
        }
        const added = accepted.find((file) => !files[kind].includes(file));
        setFiles((prev) => ({ ...prev, [kind]: accepted }));
        setReadState((prev) => ({ ...prev, [kind]: EMPTY_READ_STATE }));
        if (added) setPreviewFile(added);
    };

    const handleRemoveFile = (kind, file) => {
        setFiles((prev) => ({ ...prev, [kind]: prev[kind].filter((f) => f !== file) }));
        setReadState((prev) => ({ ...prev, [kind]: EMPTY_READ_STATE }));
    };

    const readDocument = async (kind) => {
        const body = new FormData();
        body.append('CaseId', client.caseId);
        body.append('AccountantId', client.accountantId);
        files[kind].forEach((file) => body.append('Documents', file));
        try {
            const response = kind === 'manifest'
                ? await DocumentService.CreateImportManifestByOCR(body, token)
                : await DocumentService.CreateCustomsAgentInvoiceByOCR(body, token);
            const payload = await response.json().catch(() => null);
            const warnings = Array.isArray(payload?.warnings) ? payload.warnings.filter(Boolean) : [];
            if (!response.ok || !payload?.success || !payload.parsed) {
                return { kind, ok: false, error: payload?.error || t('importManifestModal.readFailedGeneric'), warnings };
            }
            return { kind, ok: true, parsed: payload.parsed, warnings };
        } catch (err) {
            return { kind, ok: false, error: err?.message || t('importManifestModal.readFailedGeneric'), warnings: [] };
        }
    };

    /** Form values read from the documents; only non-empty values overwrite what the user already typed. */
    const buildAiValues = (results) => {
        const values = {};
        const manifest = results.find((r) => r.kind === 'manifest' && r.ok)?.parsed;
        if (manifest) {
            const manifestDate = parseServerDate(manifest.manifest_date);
            if (manifest.manifest_number) values.manifestNumber = String(manifest.manifest_number);
            if (manifestDate) values.manifestDate = manifestDate;
            values.customsPurchaseTax = manifest.customs_purchase_tax;
            values.importManifestVat = manifest.import_manifest_vat;
            values.computerFee = manifest.computer_fee;
            values.securityCheckFee = manifest.security_check_fee;
            values.variousFees = manifest.various_fees;
        }
        const invoice = results.find((r) => r.kind === 'invoice' && r.ok)?.parsed;
        if (invoice) {
            const invoiceDate = parseServerDate(invoice.invoice_date);
            if (invoice.invoice_number) values.invoiceNumber = String(invoice.invoice_number);
            if (invoiceDate) values.invoiceDate = invoiceDate;
            values.commission = invoice.commission;
            values.storageAtCustoms = invoice.storage_at_customs;
            values.seaAirFreight = invoice.sea_air_freight;
            values.variousVatLiable = invoice.various_vat_liable;
            values.variousVatExempt = invoice.various_vat_exempt;
            values.roundingDifference = invoice.rounding_difference;
        }
        AMOUNT_FIELDS.forEach((field) => {
            if (field in values) values[field] = toNumber(values[field]).toFixed(2);
        });
        return { values, manifest, invoice };
    };

    const handleRead = async () => {
        const kinds = DOC_KINDS.filter((kind) => files[kind].length > 0);
        if (kinds.length === 0) {
            toast.error(t('PleaseUploadAtLeastOneDocument'));
            return;
        }

        setIsReading(true);
        try {
            const results = await Promise.all(kinds.map(readDocument));
            const { values, manifest, invoice } = buildAiValues(results);

            const notes = {};
            if (manifest) {
                const componentsSum = MANIFEST_COMPONENT_FIELDS.reduce((sum, f) => sum + toNumber(values[f]), 0);
                const printedTotal = toNumber(manifest.total_manifest);
                if (printedTotal > 0 && Math.abs(printedTotal - componentsSum) > 1) {
                    notes.manifest = [t('importManifestModal.totalMismatch', {
                        printed: formatAmount(printedTotal),
                        sum: formatAmount(componentsSum),
                    })];
                }
            }

            setReadState((prev) => {
                const next = { ...prev };
                results.forEach((r) => {
                    next[r.kind] = {
                        status: r.ok ? 'read' : 'failed',
                        error: r.ok ? '' : r.error,
                        warnings: [...r.warnings, ...(notes[r.kind] || [])],
                    };
                });
                return next;
            });

            if (Object.keys(values).length > 0) {
                setFormData((prev) => withTotals({ ...prev, ...values }));
                setAiFields(new Set(Object.keys(values)));
                setValidationErrors({});
            }

            if (invoice?.customs_agent_id) {
                const parsedId = String(invoice.customs_agent_id).trim();
                const digits = (s) => String(s || '').replace(/\D/g, '');
                const match = accountCardOptions.find(
                    (opt) => opt.accountCaseId && digits(opt.accountCaseId) === digits(parsedId)
                );
                if (match) {
                    handleInputChange('customsAgent', match.value);
                    handleInputChange('customsAgentId', match.accountCaseId || parsedId);
                    setAiFields((prev) => new Set([...prev, 'customsAgent', 'customsAgentId']));
                } else {
                    handleInputChange('customsAgentId', parsedId);
                    setAiFields((prev) => new Set([...prev, 'customsAgentId']));
                    toast.info(t('PleaseAddCustomsAgent'));
                    openAddAccountCard('customsAgent', String(invoice.customs_agent || '').trim(), parsedId);
                }
            }

            if (results.some((r) => r.ok)) {
                toast.success(t('importManifestModal.readSuccess'));
                setStepIndex(1);
            } else {
                toast.error(t('importManifestModal.nothingRead'));
            }
        } finally {
            setIsReading(false);
        }
    };

    // ───────────── account cards ─────────────

    const openAddAccountCard = (fieldName, searchTerm = '', initialAccountCaseId = '') => {
        setAddAccountCardType(fieldName);
        setNewAccountCard({
            accountCodeName: searchTerm || '',
            accountCodeId: String(cardIdOption),
            accountCaseId: initialAccountCaseId || '',
        });
        setAddAccountCardOpen(true);
    };

    const handleChangeAfterAdd = async (newlyAddedCardId, fieldName, addedAccountCaseId = null) => {
        try {
            if (typeof onAccountCardAdded === 'function') {
                await Promise.resolve(onAccountCardAdded(newlyAddedCardId, addedAccountCaseId ?? '', undefined));
            } else if (typeof fetchEnums === 'function') {
                await fetchEnums();
            }
        } catch (e) {
            console.error('Error refetching account card options:', e);
        }
        handleInputChange(fieldName, newlyAddedCardId);
        if (fieldName === 'customsAgent' && addedAccountCaseId) {
            handleInputChange('customsAgentId', addedAccountCaseId);
        }
        setAddAccountCardOpen(false);
    };

    const handleAccountSelect = (field) => (event, option) => {
        if (option?.value1 === 'AddItem') {
            openAddAccountCard(field, accountSearchRef.current[field] || '');
            return;
        }
        handleInputChange(field, option ? option.value : '');
        if (field === 'customsAgent' && option?.accountCaseId) {
            handleInputChange('customsAgentId', option.accountCaseId);
        }
    };

    // ───────────── submit ─────────────

    const validateForm = () => {
        const required = {
            expense: 'ExpenseRequired',
            inputVat: 'InputVATRequired',
            customsAgent: 'CustomsAgentRequired',
            customsAgentId: 'CustomsAgentIdRequired',
            manifest: 'ManifestRequired',
            liableExpenses: 'LiableExpensesRequired',
            exemptExpenses: 'ExemptExpensesRequired',
        };
        const errors = {};
        Object.entries(required).forEach(([field, key]) => {
            if (!formData[field]) errors[field] = t(key);
        });
        setValidationErrors(errors);
        return errors;
    };

    const handleCreate = async () => {
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            toast.error(t('importManifestModal.requiredFieldsMissing'));
            setTimeout(() => {
                scrollRef.current?.querySelector('[data-invalid="true"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }, 0);
            return;
        }

        setIsSubmitting(true);
        try {
            const reportingMonthDate = formData.reportingMonthDate || toMonthDate(year, month - 1);
            const registrationMonthDate = formData.registrationMonthDate || toMonthDate(year, month - 1);
            const totalVatLiable = toNumber(formData.totalVatLiable);
            const totalVat = calculateVatAmountV2(totalVatLiable, 100, formData.invoiceDate);
            const entry1Amount = toNumber(formData.seaAirFreight) + toNumber(formData.variousVatExempt);
            const entry2Amount = totalVat + totalVatLiable;
            const entry3Amount = toNumber(formData.customsPurchaseTax) + toNumber(formData.computerFee)
                + toNumber(formData.securityCheckFee) + toNumber(formData.variousFees);
            const entry4Amount = toNumber(formData.importManifestVat);
            const amountOrNull = (value) => (value ? parseFloat(value) : null);

            const body = new FormData();
            body.append('CaseId', client.caseId);
            body.append('AccountantId', client.accountantId);
            body.append('DocumentDate', (formData.invoiceDate || new Date()).toISOString());
            body.append('UploadDate', new Date().toISOString());
            body.append('ReportingMonthDate', reportingMonthDate.toISOString());
            body.append('RegistrationMonthDate', registrationMonthDate.toISOString());
            body.append('AddAsProcessed', 'false');
            body.append('OurDocument', 'false');
            body.append('IsLocked', 'false');
            body.append('AccountsSection', JSON.stringify({
                Expense: formData.expense || null,
                ExpenseName: formData.expenseName || null,
                InputVat: formData.inputVat || null,
                CustomsAgent: formData.customsAgent || null,
                CustomsAgentId: formData.customsAgentId || null,
            }));
            body.append('CustomsAgentInvoiceSection', JSON.stringify({
                InvoiceNumber: formData.invoiceNumber || null,
                InvoiceDate: formData.invoiceDate || null,
                Commission: amountOrNull(formData.commission),
                StorageAtCustoms: amountOrNull(formData.storageAtCustoms),
                SeaAirFreight: amountOrNull(formData.seaAirFreight),
                VariousVatLiable: amountOrNull(formData.variousVatLiable),
                VariousVatExempt: amountOrNull(formData.variousVatExempt),
                RoundingDifference: amountOrNull(formData.roundingDifference),
            }));
            body.append('SortingCodesSection', JSON.stringify({
                Manifest: formData.manifest || null,
                LiableExpenses: formData.liableExpenses || null,
                ExemptExpenses: formData.exemptExpenses || null,
            }));
            body.append('ImportManifestDetailsSection', JSON.stringify({
                ManifestNumber: formData.manifestNumber || null,
                ManifestDate: formData.manifestDate || null,
                CustomsPurchaseTax: amountOrNull(formData.customsPurchaseTax),
                ImportManifestVat: amountOrNull(formData.importManifestVat),
                ComputerFee: amountOrNull(formData.computerFee),
                SecurityCheckFee: amountOrNull(formData.securityCheckFee),
                VariousFees: amountOrNull(formData.variousFees),
                TotalManifest: amountOrNull(formData.totalManifest),
            }));
            body.append('Entry1Amount', entry1Amount.toString());
            body.append('Entry2Amount', entry2Amount.toString());
            body.append('Entry3Amount', entry3Amount.toString());
            body.append('Entry4Amount', entry4Amount.toString());
            allFiles.forEach(({ file }) => body.append('Documents', file));

            const response = await DocumentService.CreateImportManifestEntry(body, token);
            if (!response.ok) {
                console.error('Error creating import manifest entry:', await response.text());
                toast.error(t('ErrorCreatingImportManifestEntry'));
                return;
            }
            const result = await response.json();
            if (result.createdEntries === 0) {
                toast.error(t('ErrorCreatingImportManifestEntry'));
                return;
            }

            toast.success(t('ImportManifestEntryCreatedSuccessfully'));
            if (typeof onDataRefresh === 'function') await onDataRefresh();

            // Ready for the next manifest: keep the accounts, sorting codes and months the user chose.
            setFormData((prev) => ({
                ...buildInitialFormData(year, month),
                expense: prev.expense,
                inputVat: prev.inputVat,
                manifest: prev.manifest,
                liableExpenses: prev.liableExpenses,
                exemptExpenses: prev.exemptExpenses,
                reportingMonthDate: prev.reportingMonthDate,
                registrationMonthDate: prev.registrationMonthDate,
            }));
            setFiles({ manifest: [], invoice: [] });
            setReadState({ manifest: EMPTY_READ_STATE, invoice: EMPTY_READ_STATE });
            setAiFields(new Set());
            setValidationErrors({});
            setPreviewFile(null);
            setStepIndex(0);
        } catch (error) {
            console.error('Error creating import manifest entry:', error);
            toast.error(t('ErrorUpdatingManifestEntry'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // ───────────── close ─────────────

    const handleAttemptClose = async () => {
        if (isBusy) return;
        const hasTypedText = [formData.customsAgent, formData.customsAgentId, formData.invoiceNumber, formData.manifestNumber]
            .some((v) => String(v || '').trim() !== '');
        const hasAmounts = AMOUNT_FIELDS.some((field) => toNumber(formData[field]) !== 0);
        if (!hasTypedText && !hasAmounts && !hasFiles) {
            onClose();
            return;
        }

        const result = await Swal.fire({
            title: t('ResetFieldsQuestion') || 'Reset fields before closing?',
            text: t('ResetFieldsQuestionText') || 'Would you like to reset all fields before closing the modal?',
            icon: 'question',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t('ResetAndClose') || 'Reset and Close',
            denyButtonText: t('CloseWithoutReset') || 'Close without Reset',
            cancelButtonText: t('Cancel') || 'Cancel',
        });
        if (result.isConfirmed) {
            resetAll();
            onClose();
        } else if (result.isDenied) {
            onClose();
        }
    };

    if (!open) return null;

    // ───────────── render helpers ─────────────

    const docLabel = (kind) => t(kind === 'manifest' ? 'importManifestModal.docManifest' : 'importManifestModal.docInvoice');

    const renderAiMark = (field) => aiFields.has(field) && (
        <Tooltip title={t('importManifestModal.aiFilledTooltip')} arrow placement="top">
            <span className={styles.aiMark}>AI</span>
        </Tooltip>
    );

    const fieldId = (field) => `import-manifest-${field}`;

    const inputClass = (field, extra = '') => [
        styles.input,
        extra,
        aiFields.has(field) ? styles.inputAi : '',
        validationErrors[field] ? styles.inputError : '',
    ].filter(Boolean).join(' ');

    /** One "label | value" row; every field of the review step is rendered through it. */
    const renderRow = (field, labelKey, control, { required = false } = {}) => (
        <div className={styles.fieldRow} key={field} data-invalid={!!validationErrors[field]}>
            <label className={styles.fieldLabel} htmlFor={fieldId(field)}>
                <span>{t(labelKey)}</span>
                {required && <span className={styles.required}>*</span>}
                {renderAiMark(field)}
            </label>
            <div className={styles.fieldControl}>
                {control}
                {validationErrors[field] && <span className={styles.fieldError}>{validationErrors[field]}</span>}
            </div>
        </div>
    );

    const renderAmountField = (field, labelKey) => renderRow(field, labelKey, (
        <input
            id={fieldId(field)}
            type="number"
            step="0.01"
            inputMode="decimal"
            className={inputClass(field, styles.inputAmount)}
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
            onFocus={(e) => e.target.select()}
        />
    ));

    const renderTotalRow = (field, labelKey) => (
        <div className={`${styles.fieldRow} ${styles.totalRow}`} key={field}>
            <span className={styles.fieldLabel}>{t(labelKey)}</span>
            <span className={styles.totalValue}>{formatAmount(formData[field])}</span>
        </div>
    );

    const renderTextField = (field, labelKey, { required = false } = {}) => renderRow(field, labelKey, (
        <input
            id={fieldId(field)}
            type="text"
            className={inputClass(field)}
            value={formData[field]}
            onChange={(e) => handleInputChange(field, e.target.value)}
        />
    ), { required });

    const renderDateField = (field, labelKey) => renderRow(field, labelKey, (
        <div className={aiFields.has(field) ? styles.pickerAi : undefined}>
            <CustomDatePicker
                date={formData[field] || null}
                onDateChange={(date) => handleInputChange(field, date)}
                height="38px"
            />
        </div>
    ));

    const renderMonthField = (field, labelKey) => renderRow(field, labelKey, (
        <CustomMonthDatePicker
            date={formData[field]}
            onDateChange={handleMonthChange(field)}
            height="38px"
        />
    ));

    const autocompleteInputSx = (field) => ({
        height: '38px',
        padding: '0 10px',
        borderRadius: '8px',
        background: aiFields.has(field) ? '#eef1ff' : '#fff',
        border: `1px solid ${validationErrors[field] ? '#e53e3e' : '#d5d9ee'}`,
        fontSize: '14px',
        '&.Mui-focused': { borderColor: '#304FFF', boxShadow: '0 0 0 2px rgba(48, 79, 255, 0.15)' },
    });

    const renderAutocomplete = ({ field, labelKey, options, getOptionLabel, findOption, onChange, onInputChange }) =>
        renderRow(field, labelKey, (
            <Autocomplete
                id={fieldId(field)}
                options={options}
                getOptionLabel={getOptionLabel}
                value={options.find(findOption) || null}
                onChange={onChange}
                onInputChange={onInputChange}
                PopperComponent={StyledPopper}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="standard"
                        placeholder={t(labelKey)}
                        InputProps={{ ...params.InputProps, disableUnderline: true, sx: autocompleteInputSx(field) }}
                        inputProps={{ ...params.inputProps, style: { textAlign: direction === 'ltr' ? 'left' : 'right' } }}
                    />
                )}
            />
        ), { required: true });

    const renderAccountField = (field, labelKey) => renderAutocomplete({
        field,
        labelKey,
        options: accountCardOptions || [],
        getOptionLabel: (option) => option.label ?? '',
        findOption: (option) => option.value === formData[field],
        onChange: handleAccountSelect(field),
        onInputChange: (event, value) => { accountSearchRef.current[field] = value; },
    });

    const renderSortCodeField = (field, labelKey) => renderAutocomplete({
        field,
        labelKey,
        options: sortCodeIdOptions || [],
        getOptionLabel: (option) => `${option.sortCodeId} - ${option.sortCodeName}`,
        findOption: (option) => option.sortCodeId === formData[field],
        onChange: (event, option) => handleInputChange(field, option ? option.sortCodeId : ''),
    });

    const renderStatusBadge = (kind) => {
        const { status } = readState[kind];
        if (status === 'read') {
            return (
                <span className={`${styles.statusBadge} ${styles.statusRead}`}>
                    <CheckCircleIcon /> {t('importManifestModal.statusRead')}
                </span>
            );
        }
        if (status === 'failed') {
            return (
                <span className={`${styles.statusBadge} ${styles.statusFailed}`}>
                    <ErrorOutlineIcon /> {t('importManifestModal.statusFailed')}
                </span>
            );
        }
        return files[kind].length > 0 ? (
            <span className={`${styles.statusBadge} ${styles.statusReady}`}>{t('importManifestModal.statusReady')}</span>
        ) : (
            <span className={styles.statusBadge}>{t('importManifestModal.statusNotUploaded')}</span>
        );
    };

    const renderUploadCard = (kind) => {
        const { status, error } = readState[kind];
        const cardClass = [
            styles.uploadCard,
            status === 'read' ? styles.uploadCardRead : '',
            status === 'failed' ? styles.uploadCardFailed : '',
        ].filter(Boolean).join(' ');
        return (
            <div className={cardClass} key={kind}>
                <div className={styles.uploadCardHeader}>
                    <h4 className={styles.sectionTitle}>{t(`importManifestModal.${kind}CardTitle`)}</h4>
                    {renderStatusBadge(kind)}
                </div>
                <p className={styles.muted}>{t(`importManifestModal.${kind}CardHint`)}</p>
                <div className={styles.dropzone}>
                    <DocumentDrop
                        documents={files[kind]}
                        setDocuments={() => {}}
                        handleDocumentUpload={handleFilesChange(kind)}
                        suppressLabelNames
                        hideList
                        fullWidth
                    />
                </div>
                {files[kind].length > 0 && (
                    <div className={styles.fileList}>
                        {files[kind].map((file) => (
                            <div className={styles.fileChip} key={`${file.name}-${file.size}-${file.lastModified}`}>
                                <DescriptionIcon className={styles.fileIcon} />
                                <button
                                    type="button"
                                    className={styles.fileName}
                                    title={file.name}
                                    onClick={() => { setPreviewFile(file); setShowPreview(true); }}
                                >
                                    {file.name}
                                </button>
                                <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                                <Tooltip title={t('importManifestModal.removeFile')}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveFile(kind, file)}
                                            disabled={isReading}
                                            className={styles.iconBtn}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </div>
                        ))}
                    </div>
                )}
                {status === 'failed' && error && <p className={styles.cardError}>{error}</p>}
            </div>
        );
    };

    const renderUploadStep = () => (
        <>
            <div className={styles.guide}>
                <h3 className={styles.sectionTitle}>{t('importManifestModal.howItWorksTitle')}</h3>
                <ol className={styles.guideSteps}>
                    {[1, 2, 3].map((n) => (
                        <li className={styles.guideStep} key={n}>
                            <span className={styles.guideNumber}>{n}</span>
                            <span>{t(`importManifestModal.howStep${n}`)}</span>
                        </li>
                    ))}
                </ol>
                <div className={styles.guideTip}>
                    <InfoIcon />
                    <span>{t('importManifestModal.manualHint')}</span>
                    <button type="button" className={styles.linkBtn} onClick={() => setStepIndex(1)} disabled={isBusy}>
                        {t('importManifestModal.fillManually')}
                    </button>
                </div>
            </div>

            <div className={styles.uploadGrid}>{DOC_KINDS.map(renderUploadCard)}</div>

            {isReading && (
                <div className={styles.processing} role="status">
                    <CircularProgress size={20} className={styles.spinner} />
                    <span>{t('importManifestModal.readingHint')}</span>
                </div>
            )}
        </>
    );

    const renderDocumentColumn = (kind, title, rows) => (
        <section className={styles.formSection} key={kind}>
            <div className={styles.formSectionHeader}>
                <h4 className={styles.sectionTitle}>{title}</h4>
                {readState[kind].status !== 'idle' && renderStatusBadge(kind)}
            </div>
            <div className={styles.fieldList}>{rows}</div>
        </section>
    );

    const renderReviewStep = () => {
        const readNotes = DOC_KINDS.flatMap((kind) => {
            const { status, error, warnings } = readState[kind];
            const lines = warnings.map((w) => `${docLabel(kind)}: ${w}`);
            if (status === 'failed' && error) lines.unshift(`${docLabel(kind)}: ${error}`);
            return lines;
        });

        return (
            <>
                <div className={styles.reviewHeader}>
                    <h3 className={styles.sectionTitle}>{t('importManifestModal.reviewTitle')}</h3>
                    <p className={styles.muted}>
                        {aiFields.size > 0 ? t('importManifestModal.reviewHintAi') : t('importManifestModal.reviewHintManual')}
                    </p>
                </div>

                {readNotes.length > 0 && (
                    <details className={`${styles.alert} ${styles.alertWarn}`} open>
                        <summary>{t('importManifestModal.readNotes', { count: readNotes.length })}</summary>
                        <ul className={styles.alertList}>
                            {readNotes.map((note, idx) => <li key={`${idx}-${note}`}>{note}</li>)}
                        </ul>
                    </details>
                )}

                <div className={styles.columns}>
                    {renderDocumentColumn('invoice', t('CustomsAgentInvoice'), [
                        renderAccountField('customsAgent', 'CustomsAgent'),
                        renderTextField('customsAgentId', 'CustomsAgentId', { required: true }),
                        renderTextField('invoiceNumber', 'InvoiceNumber'),
                        renderDateField('invoiceDate', 'InvoiceDate'),
                        renderAmountField('commission', 'Commission'),
                        renderAmountField('storageAtCustoms', 'StorageAtCustoms'),
                        renderAmountField('variousVatLiable', 'VariousVatLiable'),
                        renderAmountField('seaAirFreight', 'SeaAirFreight'),
                        renderAmountField('variousVatExempt', 'VariousVatExempt'),
                        renderAmountField('roundingDifference', 'RoundingDifference'),
                        renderTotalRow('totalVatLiable', 'TotalVatLiable'),
                    ])}
                    {renderDocumentColumn('manifest', t('ImportManifestDetails'), [
                        renderTextField('manifestNumber', 'ManifestNumber'),
                        renderDateField('manifestDate', 'ManifestDate'),
                        renderAmountField('customsPurchaseTax', 'CustomsPurchaseTax'),
                        renderAmountField('importManifestVat', 'ImportManifestVAT'),
                        renderAmountField('computerFee', 'ComputerFee'),
                        renderAmountField('securityCheckFee', 'SecurityCheckFee'),
                        renderAmountField('variousFees', 'VariousFees'),
                        renderTotalRow('totalManifest', 'TotalManifest'),
                    ])}
                </div>

                <section className={styles.formSection}>
                    <h4 className={styles.sectionTitle}>{t('importManifestModal.sectionGeneral')}</h4>
                    <div className={styles.columns}>
                        <div className={styles.fieldList}>
                            {renderMonthField('reportingMonthDate', 'MonthReportDate')}
                            {renderMonthField('registrationMonthDate', 'RegistrationMonthDate')}
                            {renderAccountField('expense', 'Expense')}
                            {renderAccountField('inputVat', 'InputVAT')}
                        </div>
                        <div className={styles.fieldList}>
                            <span className={styles.subTitle}>{t('SortingCodes')}</span>
                            {renderSortCodeField('manifest', 'Manifest')}
                            {renderSortCodeField('liableExpenses', 'LiableExpenses')}
                            {renderSortCodeField('exemptExpenses', 'ExemptExpenses')}
                        </div>
                    </div>
                </section>

                <div className={styles.kpis}>
                    {[
                        ['TotalVatLiable', formData.totalVatLiable],
                        ['TotalVAT', formData.totalVat],
                        ['TotalIncludingVAT', formData.totalIncludingVat],
                    ].map(([labelKey, value]) => (
                        <div className={styles.kpi} key={labelKey}>
                            <span className={styles.kpiLabel}>{t(labelKey)}</span>
                            <span className={styles.kpiValue}>{formatAmount(value)}</span>
                        </div>
                    ))}
                    <div className={`${styles.kpi} ${styles.kpiStrong}`}>
                        <span className={styles.kpiLabel}>{t('TotalToPay')}</span>
                        <span className={styles.kpiValue}>{formatAmount(formData.totalToPay)}</span>
                    </div>
                </div>
            </>
        );
    };

    const renderPreview = () => {
        const url = getFileUrl(activePreview.file);
        return (
            <div className={styles.preview}>
                {allFiles.length > 1 && (
                    <div className={styles.previewTabs}>
                        {allFiles.map(({ kind, file }) => (
                            <button
                                type="button"
                                key={`${kind}-${file.name}-${file.lastModified}`}
                                className={`${styles.previewTab}${file === activePreview.file ? ` ${styles.previewTabActive}` : ''}`}
                                onClick={() => setPreviewFile(file)}
                                title={file.name}
                            >
                                <b>{docLabel(kind)}</b>
                                <span>{file.name}</span>
                            </button>
                        ))}
                    </div>
                )}
                <div className={styles.previewFrame}>
                    {isImageFile(activePreview.file) ? (
                        <img src={url} alt={activePreview.file.name} />
                    ) : (
                        <iframe key={url} src={`${url}#view=FitH`} title={activePreview.file.name} />
                    )}
                </div>
            </div>
        );
    };

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={handleAttemptClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div
                        className={`${styles.modal}${hasPreview ? ` ${styles.modalWide}` : ''}`}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="import-manifest-modal-title"
                    >
                        <div className={styles.body}>
                            <div className={styles.header}>
                                <div className={styles.headerStart}>
                                    <Tooltip title={t('Close')}>
                                        <IconButton onClick={handleAttemptClose} className={styles.iconBtn}>
                                            <CancelIcon />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                                <div id="import-manifest-modal-title">{t('ImportManifestEntry')}</div>
                                <div className={styles.headerEnd}>
                                    {hasFiles && (
                                        <Tooltip title={t(showPreview ? 'importManifestModal.hidePreview' : 'importManifestModal.showPreview')}>
                                            <IconButton onClick={() => setShowPreview((prev) => !prev)} className={styles.iconBtn}>
                                                {showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>

                            <div className={styles.tabs}>
                                {[
                                    t('importManifestModal.stepUploadTab'),
                                    t('importManifestModal.stepReviewTab'),
                                ].map((label, index) => (
                                    <button
                                        type="button"
                                        key={label}
                                        className={`${styles.tab}${stepIndex === index ? ` ${styles.tabActive}` : ''}`}
                                        onClick={() => setStepIndex(index)}
                                        disabled={isBusy}
                                    >
                                        <span className={styles.tabIndex}><span>{index + 1}</span></span>
                                        {label}
                                    </button>
                                ))}
                            </div>

                            <div className={styles.content}>
                                <div className={styles.scroll} ref={scrollRef}>
                                    {stepIndex === 0 ? renderUploadStep() : renderReviewStep()}
                                </div>
                                {hasPreview && renderPreview()}
                            </div>

                            <div className={styles.footer}>
                                <button type="button" className={styles.secondaryBtn} onClick={handleAttemptClose} disabled={isBusy}>
                                    {t('Close')}
                                </button>
                                {stepIndex === 1 && (
                                    <button type="button" className={styles.ghostBtn} onClick={() => setStepIndex(0)} disabled={isBusy}>
                                        {t('importManifestModal.back')}
                                    </button>
                                )}
                                <div className={styles.footerSpacer} />
                                {stepIndex === 0 ? (
                                    <>
                                        <button type="button" className={styles.ghostBtn} onClick={() => setStepIndex(1)} disabled={isBusy}>
                                            {t('importManifestModal.fillManually')}
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.primaryBtn}
                                            onClick={handleRead}
                                            disabled={!hasFiles || isReading}
                                        >
                                            {isReading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
                                            {isReading ? t('importManifestModal.reading') : t('importManifestModal.readWithAi')}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button type="button" className={styles.ghostBtn} onClick={resetAll} disabled={isBusy}>
                                            {t('importManifestModal.clear')}
                                        </button>
                                        <button type="button" className={styles.primaryBtn} onClick={handleCreate} disabled={isSubmitting}>
                                            {isSubmitting && <CircularProgress size={16} color="inherit" />}
                                            {isSubmitting ? t('importManifestModal.creating') : t('importManifestModal.createEntry')}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {client?.caseId && token && (
                <AddAccountCardModal
                    accountCardOptions={accountCardOptions}
                    open={addAccountCardOpen}
                    onClose={() => setAddAccountCardOpen(false)}
                    editedItem={newAccountCard}
                    isEdit={false}
                    isRange={false}
                    isClosedReportingMonth={false}
                    palCodes={palCodes || []}
                    propPalCode=""
                    direction={direction}
                    isAdd
                    client={client}
                    token={token}
                    setLoading={setLoading || setLocalLoading || (() => {})}
                    handleChangeAfterAdd={handleChangeAfterAdd}
                    fetchEnums={fetchEnums || (async () => {})}
                    type={addAccountCardType}
                />
            )}
        </Portal>
    );
};

export default ImportManifestEntryModal;
